import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

// ─────────────────────────────────────────────────────────────
// Tailwind class merge helper (shadcn standard)
// ─────────────────────────────────────────────────────────────

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

// ─────────────────────────────────────────────────────────────
// Number formatters
// ─────────────────────────────────────────────────────────────

export function formatDownloadCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000)     return `${(count / 1_000).toFixed(1)}K`
  return count.toString()
}

// ─────────────────────────────────────────────────────────────
// Date formatters
// ─────────────────────────────────────────────────────────────

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year:  'numeric',
  month: 'long',
  day:   'numeric',
})

const relativeFormatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })

export function formatDate(dateStr: string): string {
  return dateFormatter.format(new Date(dateStr))
}

export function formatRelativeDate(dateStr: string): string {
  const date   = new Date(dateStr)
  const now    = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffS  = Math.round(diffMs / 1000)
  const diffM  = Math.round(diffS / 60)
  const diffH  = Math.round(diffM / 60)
  const diffD  = Math.round(diffH / 24)
  const diffW  = Math.round(diffD / 7)
  const diffMo = Math.round(diffD / 30)

  if (Math.abs(diffS)  < 60)  return relativeFormatter.format(diffS,  'second')
  if (Math.abs(diffM)  < 60)  return relativeFormatter.format(diffM,  'minute')
  if (Math.abs(diffH)  < 24)  return relativeFormatter.format(diffH,  'hour')
  if (Math.abs(diffD)  < 7)   return relativeFormatter.format(diffD,  'day')
  if (Math.abs(diffW)  < 5)   return relativeFormatter.format(diffW,  'week')
  return relativeFormatter.format(diffMo, 'month')
}

// ─────────────────────────────────────────────────────────────
// String helpers
// ─────────────────────────────────────────────────────────────

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function parseTags(tagString: string): string[] {
  return tagString
    .split(',')
    .map((t) => t.trim().toLowerCase().replace(/[^a-z0-9-]/g, ''))
    .filter(Boolean)
    .slice(0, 10) // max 10 tags per photo
}

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 3) + '...'
}

// ─────────────────────────────────────────────────────────────
// File helpers
// ─────────────────────────────────────────────────────────────

export function generateUniqueFilename(originalName: string): string {
  const ext       = originalName.split('.').pop() ?? 'jpg'
  const timestamp = Date.now()
  const random    = Math.random().toString(36).slice(2, 8)
  return `${timestamp}-${random}.${ext}`
}

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024)        return `${(bytes / 1024).toFixed(1)} KB`
  return `${bytes} B`
}

// ─────────────────────────────────────────────────────────────
// URL helpers
// ─────────────────────────────────────────────────────────────

export function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function getInitials(name: string | null, fallback = '?'): string {
  if (!name) return fallback
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .join('')
}
